/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild
} from '@angular/core';

import { Router }                from '@angular/router';

import {
  ValidationErrors,
  FormControl,
  FormGroup,
  Validators,
  AsyncValidatorFn,
  AbstractControl
}                                from '@angular/forms';

import {
  Observable,
  of
}                                from 'rxjs';

import {
  first,
  map,
  switchMap
} from "rxjs/operators";

import { IUserRow }              from '@/screens/administration/i.user.row';
import { Logger }                from '@/util/logger';
import { bytesToSize }           from "@/util/util";
import { MatSidenav }            from '@angular/material/sidenav';
import { UserService }           from '@/services/user.service';
import {OrganizationModel} from "@/models/organization.model";


@Component({
  standalone: false,
  templateUrl: 'user.detail.component.html'
})

/**
 *
 * @class UserDetailComponent
 *
 * @brief user details page
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2022
 *
 */
export class UserDetailComponent implements OnInit {


  @ViewChild('adminsidenav')
  public         nav: MatSidenav;

  /** the userid of the user we are editing */

  private _userid: string    = '';

  /** the existing user attributes on page load and after update click */
  public oldUser: IUserRow   = null;

  /** any changes for the user attributes */
  public newUser: IUserRow   = {
    display_name: '',
    first_name: '',
    last_name: '',
    email: '',
    userid: ''
  };

  /** validation controller for the form */
  public  form: FormGroup    = null;

  /** validation controller for the form */
  public  resetForm: FormGroup = null;

  /** users's file count */
  private usage: string      = '-';

  /** user's disk usage */
  private usageBytes: string = '0 B';

  /** confirmation of saving */
  public saveMessage: string = '';

  /** confirmation of password change */
  public resetMessage: string = '';

  /** the new password */
  public _password: string = '';

  /** the set of all possible organizations */
  public organizations: OrganizationModel[] = [];

  /** subset of organizations the approver is allowing for admin by this new user */
  private _adminOrgs: OrganizationModel[]   = [];

  private _origAdmin: OrganizationModel[]   = [];

  /**
   *  primeng inputswitch has no way to set initial on/off, you have to use a
   *  model...but we have "N" switches, and we don't know what they are in
   *  advance.  So, we have to use a map as the "model".
   *
   */
  public checkStates: object                = {};

  /** standard constructor */
  constructor(
    private router: Router,
    private users:  UserService,
    private cdRef:  ChangeDetectorRef
  ) {

  }

  /** the approver is enabling/disabling the user as an administrator on the given org */
  toggleOrgAdmin(event, key: string) {

    /* ensure org with 'key' is NOT in the admin list */

    let ud: UserDetailComponent = this;

    Logger.debug(`[UserDetail] admin toggle on ${key}:`, event.checked);

    ud.checkStates[key] = false;

    ud._adminOrgs = ud._adminOrgs.filter(x => x.key.toUpperCase() != key.toUpperCase());

    if (event.checked) {

      /* ensure org with 'key' is in the admin list */

      ud._adminOrgs.push(ud.organizations.find(x => x.key.toUpperCase() == key.toUpperCase()));

      ud.checkStates[key] = true;
    }

    ud.cdRef.detectChanges();

    Logger.debug('[UserDetail] new admin orgs: ', ud._adminOrgs);
  }

  /** test if this org is one that the user is an admin for */
  isAdminFor(key: string): boolean {

    let ud: UserDetailComponent = this;

    if (ud._adminOrgs.find(x => x.key.toUpperCase() == key.toUpperCase())) {

      return true;
    }

    return false;
  }

  /** on page setup */
  ngOnInit() {

    /* setup the validation on the form */

    let ud: UserDetailComponent = this;

    ud.form = new FormGroup({

      name: new FormControl('', [
        Validators.required,
      ]),

      firstname: new FormControl('', [
        Validators.required,
      ]),

      lastname: new FormControl('', [
        Validators.required,
      ]),

      email: new FormControl('', [
          Validators.required,
          Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$")
        ],
        this.emailExistsValidator()
      ),

      userid: new FormControl('', [
          Validators.required,
          Validators.pattern("^[a-zA-Z0-9._%+-]+$")
        ],
        this.useridExistsValidator()
      )

    });

    ud.form.statusChanges.subscribe((status: string) => {

      Logger.debug(`[UserDetail] user form status: `, status);

    });

    ud.resetForm = new FormGroup({

      password: new FormControl('', [
        Validators.required
      ])

    });


    ud.resetForm.statusChanges.subscribe((status: string) => {

      Logger.debug(`[UserDetail] rest form status: `, status);

    });

    /* what user are we editing? The URL ends with the 'userid' of the user */

    const url: string = this.router.url;

    this._userid = url.split(`/`).pop();

    Logger.debug(`[UserDetailPage] looking for user: ${this._userid} ...`);

    ud.users.dump(ud._userid)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let row: IUserRow = null;

          if (data.result) {

            row = data.result;
          }

          ud.oldUser = Object.assign({}, row);
          ud.newUser = row;

          Logger.debug(`[UserDetail] user: `, ud.newUser);

          /*
           * fill in the organizations we can choose from for immediately assigning
           * to the user, to make them an org admin directly on approval.
           *
           */

          row['all_organizations'].forEach((item) => {

            ud.organizations.push(new OrganizationModel(item));

            ud.checkStates[item.key.toUpperCase()] = false;
          });

          row['my_admin_organizations'].forEach((item) => {

            ud._adminOrgs.push(new OrganizationModel(item));

            ud._origAdmin.push(new OrganizationModel(item));

            ud.checkStates[item.key.toUpperCase()] = true;
          });

          /* update the form control */

          ud.patch(ud.newUser, row['usage'], bytesToSize(row['usageBytes']));

        },

        error: (error: any): void => {

          Logger.error(`[UserDetail] could not fetch user details: `, error);

        }

      });

  }

  /** helper to update the form display */
  private patch(user: IUserRow, usage, usageBytes) {

    let ud: UserDetailComponent = this;

    ud.form.patchValue({
      name:      user?.display_name || '',
      firstname: user?.first_name   || '',
      lastname:  user?.last_name    || '',
      email:     user?.email        || '',
      userid:    user?.userid       || ''
    });

    ud.usage      = usage;
    ud.usageBytes = usageBytes;

    Logger.debug(`[UserDetail] patch - dong change detection: `, ud.form);

    ud.cdRef.detectChanges();
  }

  /** server side validation of email, make sure its not already assigned to another user. */
  private emailExistsValidator(): AsyncValidatorFn {

    let ud: UserDetailComponent = this;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {

      return of(control.value).pipe(
        switchMap((email) => ud.users.userByEmail(email).pipe(
          map((data: any) => {

            let row: IUserRow = null;

            if (data.result) {

              row = data.result;
            }

            return (row && (row.userid != ud.oldUser.userid)) ? { exists: true } : null;
          })
        ))
      );
    };
  }

  /** server side validation of usuerid, make sure its not already assigned to another user.*/
  private useridExistsValidator(): AsyncValidatorFn {

    let ud: UserDetailComponent = this;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {

      return of(control.value).pipe(
        switchMap((userid) => ud.users.userByUserid(userid).pipe(
          map((data: any) => {

            let row: IUserRow = null;

            if (data.result) {

              row = data.result;
            }

            return (row && (row.userid != ud.oldUser.userid)) ? {exists: true} : null;
          })
        ))
      );
    };
  }

  /** helper to update a field in the newUser row */
  updateNewUser(field: string, value: any) {

    let ud: UserDetailComponent = this;

    ud.saveMessage = '';

    if (!ud.newUser) {

      return ;
    }

    ud.newUser[field] = value;

    ud.cdRef.detectChanges();
  }

  /** update the name */
  setName(event: any) {

    this.updateNewUser('display_name', event.target.value);
  }

  /** update the first name */
  setFirstName(event: any) {

    this.updateNewUser('first_name', event.target.value);
  }

  /** update the last name */
  setLastName(event: any) {

    this.updateNewUser('last_name', event.target.value);
  }

  /** update the email */
  setEMail(event: any) {

    this.updateNewUser('email', event.target.value);
  }

  /** update the email */
  setUserid(event: any) {

    this.updateNewUser('userid', event.target.value);
  }

  /** update the password reset value */
  setPassword(event: any) {

    this._password = event.target.value;
  }

  /** getter for display_name */
  get name() { return this.form.get('name'); }

  /** getter for first_name */
  get firstname() { return this.form.get('firstname'); }

  /** getter for last_name */
  get lastname() { return this.form.get('lastname'); }

  /** getter for email */
  get email() { return this.form.get('email'); }

  /** getter for userid */
  get userid() { return this.form.get('userid'); }

  /** getter for password */
  get password() { return this.resetForm.get('password')}

  /** getter for the profile picture */
  profilePicture() {

    if (this.newUser && this.newUser.options && this.newUser.options.picture) {

      return this.newUser.options.picture;
    }

    return 'assets/images/profile-512.png';
  }

  /** open or close the side nav panel */
  toggleSidebar() {

    this.nav.toggle();
  }

  /** update the database */
  save() {

    Logger.debug(`[UserDetail] saving: `, this.newUser);

    let ud: UserDetailComponent = this;
    let edit: any = {};

    ['userid', 'email', 'display_name', 'last_name', 'first_name'].forEach( (field) => {

      if (ud.newUser[field] == ud.oldUser[field]) {

        /* hasn't changed */

        return;
      }

      edit[field] = ud.newUser[field];

    });

    /* don't update if there are no changes */

    if (ud._adminOrgs.length == ud._origAdmin.length) {

      /* there was no change in the org admin links, changes elsewhere? */

      if (Object.keys(edit).length == 0) {

        /* no changes */

        ud.saveMessage = "No changes.";

        return;
      }
    }

    ud.users.update(ud.oldUser.userid, edit)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let row: IUserRow = null;

          if (data.result) {

            row = data.result;
          }

          Logger.debug(`[UserDetail] saved: `, row);

          ud.oldUser = Object.assign({}, row);
          ud.newUser = row;

          /* update the form control */

          ud.patch(ud.newUser, row['usage'], bytesToSize(row['usageBytes']));

          ud.saveMessage = "Saved!";

          /*
           * if we have any organizations that the approver has assigned for administration,
           * then do that update as well.
           *
           */

          if (ud._adminOrgs.length != ud._origAdmin.length) {

            ud._origAdmin = [...ud._adminOrgs];

            /* we also have to make the user an admin of those organizations... */

            ud.users.setAdminOrg(ud.oldUser.userid, ud._adminOrgs.map(x => x.key))
              .pipe(first())
              .subscribe({

                next: (data: any) => {

                  Logger.debug(`[UserDetail] user linked as admin to orgs!`, data);

                  ud.cdRef.detectChanges();
                },

                error: (error: any): void => {

                  Logger.debug(`[UserDetail] could not assign orgs to admin: `, error);

                  ud.saveMessage = error.toString();

                }

              });
          }
        },

        error: (error: any): void => {

          Logger.error(`[UserDetail] could not save: `, error);

          ud.saveMessage = error.toString();

        }

      });

  }

  /** reset the user's password */
  public resetPassword() {

    Logger.debug(`[UserDetail] resetting password: ${this._password}`);

    let ud: UserDetailComponent = this;

    ud.users.update(ud.oldUser.userid, {password: this._password} as IUserRow)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let row: IUserRow = null;

          if (data.result) {

            row = data.result;
          }

          Logger.debug(`[UserDetail] password reset! `, row);

          ud.oldUser     = Object.assign({}, row);
          ud.newUser     = row;

          /* update the form control */

          ud.patch(ud.newUser, row['usage'], bytesToSize(row['usageBytes']));

          ud.resetMessage = "Password has been set!";

          ud._password     = '';

          ud.cdRef.detectChanges();
        },

        error: (error: any): void => {

          Logger.error(`[UserDetail] could not set password: `, error);

          ud.resetMessage = error.toString();

        }

      });

  }

}

